import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { userService } from "@/services/user.service";
import { getUserData } from "@/utils/userData";
import { fetchWrapper } from "@/helpers/fetch-wrapper";

const ChatSocketContext = createContext();

export const ChatSocketProvider = ({ children }) => {
	const wsRef = useRef(null);
	const listenersRef = useRef(new Set());
	const [connectionStatus, setConnectionStatus] = useState("connecting");
	const [unreadCount, setUnreadCount] = useState(0);
	const [notificationCount, setNotificationCount] = useState(0);
	const { userData } = getUserData();

	const refreshUnread = useCallback(async () => {
		if (!userService.userValue) return;
		try {
			const { count } = await fetchWrapper.get("/api/internchat/unread");
			setUnreadCount(count);
		} catch (error) {
			console.error("Error fetching unread chat count:", error);
		}
	}, []);

	const refreshNotifications = useCallback(async () => {
		if (!userService.userValue) return;
		try {
			const { count } = await fetchWrapper.get("/api/users/notifications/unread-count");
			setNotificationCount(count);
		} catch (error) {
			console.error("Error fetching notification count:", error);
		}
	}, []);

	const markNotificationsRead = useCallback(async () => {
		if (!userService.userValue) return;
		try {
			await fetchWrapper.post("/api/users/notifications/mark-read");
			setNotificationCount(0);
		} catch (error) {
			console.error("Error marking notifications as read:", error);
		}
	}, []);

	const subscribe = useCallback((handler) => {
		listenersRef.current.add(handler);
		return () => listenersRef.current.delete(handler);
	}, []);

	const sendMessage = useCallback((payload) => {
		if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
			wsRef.current.send(JSON.stringify(payload));
		}
	}, []);

	const markAsRead = useCallback(async () => {
		if (!userService.userValue) return;
		try {
			const result = await fetchWrapper.post("/api/internchat/unread");
			setUnreadCount(0);
			sendMessage({ type: "chat-read", payload: result });
		} catch (error) {
			console.error("Error marking chat as read:", error);
		}
	}, [sendMessage]);

	useEffect(() => {
		refreshUnread();
		refreshNotifications();
	}, [refreshUnread, refreshNotifications]);

	useEffect(() => {
		if (!userService.userValue) return;

		const ws = new WebSocket(`wss://app.all-consulting-lascu.ro/api/ws`);
		wsRef.current = ws;

		ws.onopen = () => setConnectionStatus("connected");
		ws.onclose = () => setConnectionStatus("disconnected");

		ws.addEventListener("message", (event) => {
			try {
				const data = JSON.parse(event.data);

				if (data.type === "chat-message") {
					if (data.payload?.userID !== userData?.id) {
						setUnreadCount((prev) => prev + 1);
					}
				} else if (data.type === "notification") {
					if (data.payload?.userID === userData?.id) {
						setNotificationCount((prev) => prev + 1);
					}
				}

				listenersRef.current.forEach((handler) => handler(data));
			} catch (error) {
				console.error("Error parsing chat socket message:", error);
			}
		});

		const pingInterval = setInterval(() => {
			if (ws.readyState === WebSocket.OPEN) {
				ws.send(JSON.stringify({ event: "ping" }));
			}
		}, 29000);

		const onFocus = () => {
			refreshUnread();
			refreshNotifications();
		};
		window.addEventListener("focus", onFocus);
		const driftInterval = setInterval(onFocus, 60000);

		return () => {
			clearInterval(pingInterval);
			clearInterval(driftInterval);
			window.removeEventListener("focus", onFocus);
			ws.close();
		};
	}, [userData?.id, refreshUnread, refreshNotifications]);

	return (
		<ChatSocketContext.Provider
			value={{
				subscribe,
				sendMessage,
				connectionStatus,
				unreadCount,
				refreshUnread,
				markAsRead,
				notificationCount,
				refreshNotifications,
				markNotificationsRead,
			}}
		>
			{children}
		</ChatSocketContext.Provider>
	);
};

export const useChatSocket = () => useContext(ChatSocketContext);
